import React, { useEffect, useRef, useState } from 'react';
import { GridBackground } from '../GridBackground';

/**
 * ScrollTransitionGridContainer
 * 
 * Efecto "Curtain Reveal" ascendente:
 * Mientras el usuario hace scroll al final del Hero (que es sticky),
 * este contenedor sube como una cortina sólida superpuesta sobre el viewport
 * del Hero, revelando la sección de Tarifas como si fuera un panel
 * que asciende cubriendo el video.
 * 
 * Internamente también ejecuta la transición de color del grid
 * (gray-900 → crema #e7e1d9) entre Tarifas y Resultados.
 */
export function ScrollTransitionGridContainer({ children }) {
  const containerRef = useRef(null);
  const [isCrema, setIsCrema] = useState(false);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const tarifaEl = document.getElementById('tarifas');
          if (tarifaEl) {
            const rect = tarifaEl.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            // Se activa de tope en cuanto se cubre la mitad de la sección Tarifas:
            const halfCovered = rect.bottom < windowHeight * 0.50 || -rect.top > rect.height * 0.45;
            setIsCrema(halfCovered);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cambio de color al crema de Resultados:
  // Cambio rápido y directo ("de tope", 100ms) sin el efecto lento de que se va aclarando/prendiendo
  const backgroundColor = isCrema ? '#e7e1d9' : '#111827';
  const gridColor = isCrema ? 'rgba(100, 95, 90, 0.16)' : '#444444';

  // AJUSTE DE LA CORTINA:
  // '-100vh' asegura que la cortina suba cubriendo el 100% de la pantalla mientras el Hero
  // continúa fijado en top: 0, evitando que el Hero sea empujado hacia arriba a mitad de camino.
  const curtainOffset = '-100vh';

  return (
    <div
      ref={containerRef}
      className="relative z-20 w-full overflow-hidden rounded-t-[2.5rem] sm:rounded-t-[3rem] shadow-[0_-20px_60px_rgba(0,0,0,0.85)] border-t border-white/5 border-b-0 outline-none"
      style={{
        backgroundColor,
        marginTop: curtainOffset,
        transition: 'background-color 100ms ease-out',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    >
      {/* Malla continua unificada - sin máscara en modo crema para evitar cualquier línea o sombra divisoria */}
      <GridBackground
        gridColor={gridColor}
        className="opacity-90 transition-opacity duration-100"
        mask={!isCrema}
        maskClassName="transition-opacity duration-100 bg-black/60 [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]"
      />

      {/* Sutil halo ambiental que se apaga de tope al cambiar a crema */}
      <div
        className="absolute top-1/4 -left-40 w-96 h-96 rounded-full blur-3xl pointer-events-none transition-opacity duration-100"
        style={{
          backgroundColor: 'rgba(37, 99, 235, 0.15)',
          opacity: isCrema ? 0 : 0.15,
        }}
      />

      {/* Contenido (TarifaSection) */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}

export default ScrollTransitionGridContainer;
